import React, {useEffect, useState} from 'react';
import styles from './EditAdminProjectNote.module.css';
import Button from "../../common/button/Button";
import {useNavigate, useParams} from "react-router-dom";
import {adminProjectNoteAPI, updateAdminProjectNoteAPI} from "../../../services/admin";
import {AdminProjectNoteResponseData, UpdateAdminProjectNoteRequest} from "../../../types/admin";
import FileInput from "../../common/input/FileInputPanel";
import AdminLayout from "../layout/AdminLayout";
import {XCircleIcon} from "@heroicons/react/24/solid";
import {useAlert} from "../../common/error/AlertContext";
//어드민 프로젝트 특이사항 수정
const EditAdminProjectNote: React.FC = () => {
    const [data, setData] = React.useState<AdminProjectNoteResponseData | null>(null);
    const {id} = useParams<{ id: string }>();
    const [loading, setLoading] = React.useState(true);
    const navigate = useNavigate();
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [sampleName, setSampleName] = useState<string | null>(null);
    const {showAlert} = useAlert();

    const [formData, setFormData] = useState<UpdateAdminProjectNoteRequest>({
        projectId: 0,
        note: '',
        isPublic: false,
        deleteFile: false
    });

    const handleSubmit = async () => {
        try {
            //어드민 프로젝트 특이사항 수정 API
            await updateAdminProjectNoteAPI(formData, uploadedFile);
            showAlert('수정되었습니다.', "info");
            //어드민 프로젝트 조회 페이지로 이동
            navigate(`/admin/projects/${id}`);
        } catch (error) {
            console.error('Error updating project request:', error);
            alert('수정 중 오류가 발생했습니다.')
        }
    };

    //파일 업로드 가능 확장자
    const ALLOWED_IMAGE_TYPES = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp'
    ];

    //확장자 검증
    const validateImageFile = (file: File): boolean => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            showAlert('이미지 파일만 업로드 가능합니다.', "error");
            return false;
        }
        return true;
    };

    //업로드할 파일 선택
    const handleFileSelect = (file: File) => {
        if (!validateImageFile(file)) return;
        setUploadedFile(file);
    };

    //업로드 파일 삭제
    const handleDeleteSample = () => {
        setFormData(prev => ({
            ...prev,
            deleteFile: true
        }));
        setSampleName(null);
    };

    const fetchData = async () => {
        if (!id) return;
        try {
            setLoading(true);
            //어드민 프로젝트 특이사항 조회 API
            const response = await adminProjectNoteAPI(parseInt(id));
            setData(response);
            //샘플 데이터 파일명 설정
            if (response.imageName) {
                setSampleName(response.imageName);
            }
        } catch (error) {
            console.error('Error fetching project request:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    useEffect(() => {
        if (data) {
            setFormData({
                projectId: data.projectId || 0,
                note: data.note || '',
                isPublic: data.isPublic || false,
                deleteFile: false
            });
        }
    }, [data]);

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <AdminLayout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <h1 className={styles.title}>Project Note</h1>
                        <p className={styles.subtitle}>프로젝트 특이사항</p>
                    </div>
                    <div className={styles.buttonGroup}>
                        {/*저장 버튼*/}
                        <Button
                            variant="primary"
                            size="medium"
                            onClick={handleSubmit}
                        >
                            저장
                        </Button>
                        {/*취소 버튼*/}
                        <Button
                            variant="secondary"
                            size="medium"
                            onClick={() => navigate(`/admin/projects/${id}`)}
                        >
                            취소
                        </Button>
                    </div>
                </div>

                <div className={styles.mainContent}>
                    <div className={styles.leftSection}>
                        <div className={styles.uploadSection}>
                            {/*샘플 데이터 업로드*/}
                            <FileInput
                                onFileSelect={handleFileSelect}
                                accept={ALLOWED_IMAGE_TYPES.join(',')}
                                maxSize={5 * 1024 * 1024}
                            />
                            {/*첨부파일 삭제 버튼*/}
                            {sampleName && !formData.deleteFile && (
                                <div className={styles.sampleFile}>
                                    <span className={styles.fileName}>{sampleName}</span>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={handleDeleteSample}
                                        type="button"
                                    >
                                        <XCircleIcon className={styles.deleteIcon}/>
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className={styles.projectTitle}>
                            <h2>{data?.projectName}</h2>
                            <div className={styles.publicToggleContainer}>
                                <div className={styles.publicToggle}>
                                    <span>특이사항 공개 여부</span>
                                    <div className={styles.slide}>
                                        <span>{formData.isPublic ? '공개' : '비공개'}</span>
                                        <div className={styles.toggle}>
                                            {/*특이사항 공개 여부 설정, 설정에 따라 클라이언트 접근 허용/차단*/}
                                            <input
                                                type="checkbox"
                                                checked={formData.isPublic}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    isPublic: e.target.checked
                                                }))}
                                            />
                                            <span className={styles.slider}></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        {/*프로젝트 특이사항 입력*/}
                        <textarea
                            className={styles.projectExplanation}
                            placeholder="Project Note"
                            value={formData.note}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                note: e.target.value
                            }))}
                        />
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default EditAdminProjectNote;